import React, { useState } from 'react';
import { X, User, Phone, Mail, Building2, FileText, MapPin, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const AddCustomerModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    companyName: '',
    gstin: '',
    billingAddress: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    shippingAddress: {
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India'
    },
    outstandingAmount: 0
  });
  
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [loading, setLoading] = useState(false);
  const [createdCustomer, setCreatedCustomer] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (type, field, value) => {
    setFormData(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('customerAdminToken');

      const submitData = {
        ...formData,
        shippingAddress: sameAsBilling ? formData.billingAddress : formData.shippingAddress
      };

      const response = await fetch(`${backendUrl}/api/customer-admin/customers/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `Server error: ${response.status}` }));
        toast.error(errorData.detail || 'Failed to add customer');
        return;
      }

      const data = await response.json();
      
      toast.success('Customer added successfully!');
      
      // Store created customer data with credentials
      setCreatedCustomer(data.customer);
      onSuccess(data.customer);
      
    } catch (error) {
      console.error('Error adding customer:', error);
      toast.error('Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCredentials = async () => {
    if (!createdCustomer) return;
    
    setSendingEmail(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.VITE_BACKEND_URL;
      const token = localStorage.getItem('customerAdminToken');

      const response = await fetch(`${backendUrl}/api/customer-admin/customers/${createdCustomer.id}/send-credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to send email' }));
        toast.error(errorData.detail || 'Failed to send credentials email');
        return;
      }

      const data = await response.json();
      
      if (data.sent) {
        toast.success(`✅ Credentials sent to ${createdCustomer.email}`);
        setCreatedCustomer(prev => ({ ...prev, credentialsSent: true }));
      } else {
        toast.warning('Email service unavailable. Credentials shown below.');
      }
      
    } catch (error) {
      console.error('Error sending credentials:', error);
      toast.error('Failed to send credentials email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCloseAndReset = () => {
    // Reset form and state
    setFormData({
      firstName: '',
      lastName: '',
      mobile: '',
      email: '',
      companyName: '',
      gstin: '',
      billingAddress: { address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India' },
      shippingAddress: { address_line1: '', address_line2: '', city: '', state: '', pincode: '', country: 'India' },
      outstandingAmount: 0
    });
    setCreatedCustomer(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 flex justify-between items-center rounded-t-2xl">
          <div className="flex items-center gap-3">
            <User className="w-6 h-6" />
            <h2 className="text-2xl font-bold">{createdCustomer ? 'Customer Created!' : 'Add New Customer'}</h2>
          </div>
          <button onClick={handleCloseAndReset} className="hover:bg-white/20 rounded-full p-2 transition">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form - Only show if customer not created */}
        {!createdCustomer && (
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Personal Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Phone className="inline w-4 h-4 mr-1" />
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+91 9876543210"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Mail className="inline w-4 h-4 mr-1" />
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>
            </div>
          </div>

          {/* Company & GST Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-green-600" />
              Company & GST Information (Optional)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ABC Pvt Ltd"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <FileText className="inline w-4 h-4 mr-1" />
                  GSTIN
                </label>
                <input
                  type="text"
                  name="gstin"
                  value={formData.gstin}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="22AAAAA0000A1Z5"
                />
              </div>
            </div>
          </div>

          {/* Billing Address */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-purple-600" />
              Billing Address <span className="text-red-500">*</span>
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <input
                type="text"
                value={formData.billingAddress.address_line1}
                onChange={(e) => handleAddressChange('billingAddress', 'address_line1', e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Address Line 1"
              />
              <input
                type="text"
                value={formData.billingAddress.address_line2}
                onChange={(e) => handleAddressChange('billingAddress', 'address_line2', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Address Line 2 (Optional)"
              />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <input
                  type="text"
                  value={formData.billingAddress.city}
                  onChange={(e) => handleAddressChange('billingAddress', 'city', e.target.value)}
                  required
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="City"
                />
                <input
                  type="text"
                  value={formData.billingAddress.state}
                  onChange={(e) => handleAddressChange('billingAddress', 'state', e.target.value)}
                  required
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="State"
                />
                <input
                  type="text"
                  value={formData.billingAddress.pincode}
                  onChange={(e) => handleAddressChange('billingAddress', 'pincode', e.target.value)}
                  required
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Pincode"
                />
              </div>
            </div>
          </div>

          {/* Shipping Address */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-600" />
                Shipping Address
              </h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsBilling}
                  onChange={(e) => setSameAsBilling(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-600">Same as Billing</span>
              </label>
            </div>
            
            {!sameAsBilling && (
              <div className="grid grid-cols-1 gap-4">
                <input
                  type="text"
                  value={formData.shippingAddress.address_line1}
                  onChange={(e) => handleAddressChange('shippingAddress', 'address_line1', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Address Line 1"
                />
                <input
                  type="text"
                  value={formData.shippingAddress.address_line2}
                  onChange={(e) => handleAddressChange('shippingAddress', 'address_line2', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Address Line 2 (Optional)"
                />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <input
                    type="text"
                    value={formData.shippingAddress.city}
                    onChange={(e) => handleAddressChange('shippingAddress', 'city', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="City"
                  />
                  <input
                    type="text"
                    value={formData.shippingAddress.state}
                    onChange={(e) => handleAddressChange('shippingAddress', 'state', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="State"
                  />
                  <input
                    type="text"
                    value={formData.shippingAddress.pincode}
                    onChange={(e) => handleAddressChange('shippingAddress', 'pincode', e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Pincode"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Outstanding Amount */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-red-600" />
              Financial Information
            </h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Outstanding Amount (₹)</label>
              <input
                type="number"
                name="outstandingAmount"
                value={formData.outstandingAmount}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleCloseAndReset}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || createdCustomer}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-semibold disabled:opacity-50"
            >
              {loading ? 'Adding...' : createdCustomer ? 'Customer Added ✓' : 'Add Customer'}
            </button>
          </div>
        </form>
        )}

        {/* Customer Credentials Display */}
        {createdCustomer && (
          <div className="px-6 pb-6">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-500 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-800">Customer Created Successfully!</h3>
                  <p className="text-sm text-green-600">Login credentials generated</p>
                </div>
              </div>

              <div className="bg-white rounded-lg p-4 space-y-3 mb-4">
                <div className="border-b pb-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Customer Name</label>
                  <p className="text-lg font-bold text-gray-900">{createdCustomer.firstName} {createdCustomer.lastName}</p>
                </div>
                
                <div className="border-b pb-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Username / Email</label>
                  <p className="text-md font-mono text-blue-600">{createdCustomer.username || createdCustomer.email}</p>
                </div>
                
                <div className="border-b pb-2">
                  <label className="text-xs font-semibold text-gray-500 uppercase">Password</label>
                  <p className="text-md font-mono text-purple-600 font-bold">{createdCustomer.password}</p>
                </div>
                
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase">Account ID</label>
                  <p className="text-md font-mono text-gray-700">{createdCustomer.accountId}</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSendCredentials}
                  disabled={sendingEmail || createdCustomer.credentialsSent}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {sendingEmail ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : createdCustomer.credentialsSent ? (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      Email Sent ✓
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      Send Credentials via Email
                    </>
                  )}
                </button>
                
                <button
                  onClick={handleCloseAndReset}
                  className="px-6 py-3 border-2 border-green-600 text-green-700 rounded-lg hover:bg-green-50 transition font-semibold"
                >
                  Close
                </button>
              </div>

              <p className="text-xs text-gray-600 mt-3 text-center">
                💡 Tip: Save these credentials or send them via email to the customer
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddCustomerModal;
